import { Component } from '@verndale/core';

class AlertBanners extends Component {
  // Storage Key per alert group
  private get storageKey() {
    const group = (this.el as HTMLElement).dataset.bannerGroup;
    return `dismissedAlertBanners_${group}`;
  }

  override setupDefaults() {
    this.sortBannersByPriority();
    this.checkDismissedBanners();
  }

  override addListeners() {
    const closeButtons = this.el.querySelectorAll<HTMLButtonElement>('.alert-banner__close-button');

    closeButtons.forEach(button => {
      button.addEventListener('click', this.handleDismiss.bind(this));
    });
  }

  sortBannersByPriority() {
    const container = this.el;
    const banners = Array.from(container.querySelectorAll('.alert-banner'));

    const priority = {
      high: 1,
      mid: 2,
      low: 3
    };

    // Get alert level; always returns a valid value
    const getLevel = (banner: Element): 'high' | 'mid' | 'low' => {
      if (!banner || !banner.className) return 'low';
      const className = banner.className.trim();
      const match = className.match(/alert-banner--(high|mid|low)/);
      return match && match[1] ? (match[1] as 'high' | 'mid' | 'low') : 'low';
    };

    // Sort to prevent undefined errors
    const sorted = banners
      .filter(Boolean) // remove any accidental undefined/null values
      .sort((a, b) => {
        const levelA = getLevel(a);
        const levelB = getLevel(b);
        return (priority[levelA] ?? 99) - (priority[levelB] ?? 99);
      });

    sorted.forEach(b => container.appendChild(b));
  }

  checkDismissedBanners() {
    const dismissedIds = this.getDismissedIds();
    const banners = this.el.querySelectorAll<HTMLElement>('.alert-banner');

    banners.forEach(banner => {
      const bannerId = banner.id;
      if (bannerId && dismissedIds.includes(bannerId)) {
        banner.remove();
      }
    });
  }

  getDismissedIds(): string[] {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  saveDismissedId(id: string) {
    try {
      const dismissedIds = this.getDismissedIds();
      if (!dismissedIds.includes(id)) {
        dismissedIds.push(id);
        sessionStorage.setItem(this.storageKey, JSON.stringify(dismissedIds));
      }
    } catch {
      // SessionStorage not available or quota exceeded
    }
  }

  handleDismiss(e: Event) {
    const button = e.currentTarget as HTMLButtonElement;
    const banner = button.closest('.alert-banner') as HTMLElement;

    if (banner) {
      const bannerId = banner.id;

      // Store dismissed state
      if (bannerId) {
        this.saveDismissedId(bannerId);
      }

      // Add fade out animation
      const duration =
        getComputedStyle(banner).getPropertyValue('--transition-duration-short') || '0.2s';

      banner.style.transition = `opacity ${duration} ease-out, transform ${duration} ease-out`;
      banner.style.opacity = '0';
      banner.style.transform = 'translateY(-10px)';

      // Remove from DOM after animation
      setTimeout(() => {
        banner.remove();
      }, 200);
    }
  }
}

export default AlertBanners;
